<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { QrcodeStream, DetectedBarcode } from "vue-qrcode-reader";
import * as Sentry from "@sentry/vue";
import AppButton from "@/components/AppButton.vue";
import AppModal from "@/components/AppModal.vue";
import AppSelect from "@/components/AppSelect.vue";
import AppLink from "@/components/AppLink.vue";
import useDeviceDetection from "@/modules/useDeviceDetection";
import { ca } from "date-fns/locale";

const emit = defineEmits(["scanned", "scannedError"]);

interface Props {
  isBusy: boolean;
  label?: string;
  disabled?: boolean;
}

interface CameraDevice {
  deviceId: string;
  label: string;
}

interface SelectOption {
  id: string;
  name: string;
}

const props = withDefaults(defineProps<Props>(),
  {
    isBusy: false,
    label: "Scan",
    disabled: false,
  }
);

const [Platform] = useDeviceDetection();
const isIOS = computed(() => Platform.value === "ios");

const showQR = ref(false);
const availableCameras = ref<CameraDevice[]>([]);
const selectedCameraId = ref<string | null>(null);
const cameraConstraints = ref<any>({});
const showCameraSettings = ref(false);
const isResettingCamera = ref(false);
const cameraKey = ref(0); // Force re-render of QrcodeStream when camera changes
const isCameraReady = ref(false);
const isCameraLoading = ref(false);

// Computed property for camera options in select dropdown
const cameraOptions = computed<SelectOption[]>(() => {
  if (!availableCameras.value || availableCameras.value.length === 0) {
    return [];
  }

  const options = availableCameras.value.map(camera => ({
    id: camera.deviceId,
    name: camera.label || `Camera ${camera.deviceId.slice(0, 8)}...`
  }));

  return options;
});

// Enumerate available video devices
const enumerateCameras = async (): Promise<void> => {
  try {
    console.log('Enumerating cameras for platform:', Platform.value);

    // Check if mediaDevices API is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('MediaDevices API not available');
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    console.log('Found video devices:', videoDevices.length);
    videoDevices.forEach((device, index) => {
      console.log(`Camera ${index}:`, device.label || 'Unlabeled', device.deviceId);
    });

    availableCameras.value = videoDevices.map(device => ({
      deviceId: device.deviceId,
      label: device.label || `Camera ${device.deviceId.slice(0, 8)}...`
    }));

    console.log('Available cameras:', availableCameras.value);

    // Auto-select preferred camera for all platforms
    if (availableCameras.value.length > 0) {
      selectPreferredCamera();
    }
  } catch (error) {
    console.error('Error enumerating cameras:', error);
    Sentry.captureException(error);
  }
};

// Select preferred camera based on label keywords
const selectPreferredCamera = (): void => {
  const cameras = availableCameras.value;

  if (!cameras || cameras.length === 0) {
    selectedCameraId.value = null;
    return;
  }

  console.log('Selecting preferred camera from:', cameras.map(c => ({ label: c.label, id: c.deviceId })));

  // Priority order for rear cameras:
  // 1. back (not macro/depth/ultra-wide)
  // 2. rear (not macro/depth/ultra-wide)
  // 3. environment facing
  // 4. wide (not macro/depth/front)
  // 5. main camera
  // 6. first available

  let preferredCamera = cameras.find(camera => {
    const label = camera.label.toLowerCase();
    return label.includes('back') &&
           !label.includes('macro') &&
           !label.includes('depth') &&
           !label.includes('ultra-wide') &&
           !label.includes('ultrawide');
  });

  if (!preferredCamera) {
    preferredCamera = cameras.find(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('rear') &&
             !label.includes('macro') &&
             !label.includes('depth') &&
             !label.includes('ultra-wide') &&
             !label.includes('ultrawide');
    });
  }

  if (!preferredCamera) {
    preferredCamera = cameras.find(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('environment');
    });
  }

  if (!preferredCamera) {
    preferredCamera = cameras.find(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('wide') &&
             !label.includes('macro') &&
             !label.includes('depth') &&
             !label.includes('front') &&
             !label.includes('ultra-wide') &&
             !label.includes('ultrawide');
    });
  }

  if (!preferredCamera) {
    preferredCamera = cameras.find(camera => {
      const label = camera.label.toLowerCase();
      return label.includes('main') &&
             !label.includes('front');
    });
  }

  // If still no preferred camera found, use the last camera (often rear on Android)
  if (!preferredCamera) {
    preferredCamera = cameras[cameras.length - 1];
  }

  selectedCameraId.value = preferredCamera.deviceId;
  updateCameraConstraints();

  console.log('Selected preferred camera:', preferredCamera.label, preferredCamera.deviceId);
};

// Update camera constraints based on selected camera
const updateCameraConstraints = (): void => {
  if (selectedCameraId.value) {
    // For Android Chrome, try both deviceId and facingMode as fallback
    if (Platform.value === "android") {
      cameraConstraints.value = {
        deviceId: { exact: selectedCameraId.value }
      };
    } else {
      cameraConstraints.value = {
        deviceId: { exact: selectedCameraId.value }
      };
    }
  } else {
    cameraConstraints.value = {
      facingMode: 'environment'
    };
  }

  console.log('Updated camera constraints:', cameraConstraints.value);
};

// Switch to selected camera
const switchCamera = async (deviceId: string): Promise<void> => {
  console.log('Switching camera to:', deviceId, 'from', selectedCameraId.value);
  //log labels as well
  const oldCamera = availableCameras.value.find(camera => camera.deviceId === selectedCameraId.value);
  const newCamera = availableCameras.value.find(camera => camera.deviceId === deviceId);
  console.log('Switching camera from', oldCamera?.label, 'to', newCamera?.label);
  console.log('Switching camera from', selectedCameraId.value, 'to', deviceId);

  // Reset camera state when switching
  isCameraReady.value = false;
  isCameraLoading.value = true;

  selectedCameraId.value = deviceId;
  updateCameraConstraints();
  console.log(cameraKey.value);
  // Force re-render of QrcodeStream component
  cameraKey.value += 1;
  console.log(cameraKey.value);
  console.log('Camera switched to:', deviceId, 'with constraints:', cameraConstraints.value);
};

// Reset camera
const resetCamera = async (): Promise<void> => {
  if (isResettingCamera.value) return;

  isResettingCamera.value = true;

  try {
    isCameraReady.value = false;
    isCameraLoading.value = true;
    updateCameraConstraints();
    cameraKey.value += 1;
  } catch (error) {
    console.error('Error resetting camera:', error);
    Sentry.captureException(error);
  } finally {
    isResettingCamera.value = false;
  }
};

// Handle camera ready event
const onCameraOn = async (): Promise<void> => {
  console.log('Camera stream is ready');
  // Ignore initial camera-on event when component mounts due to possible timing issues
  if (cameraKey.value === 0) {
    cameraKey.value += 1;
    return;
  }
  isCameraLoading.value = false;

  // Add a small delay to ensure camera stream is fully stabilized
  setTimeout(() => {
    isCameraReady.value = true;
    console.log('Camera detection ready');
  }, 500);
};

const scanCode = async (results: DetectedBarcode[]) => {
  // Only process scans when camera is ready
  if (!isCameraReady.value) {
    console.log('Camera not ready yet, ignoring scan result');
    return;
  }

  console.log('Scanned code:', results);
  const content = results[0].rawValue;
  if (content !== null) {
    emit("scanned", content);
    showQR.value = false;
  } else {
    emit("scannedError", "No content found in scanned code");
  }
};

const onError = (error: any) => {
  showQR.value = false;
  Sentry.captureException(error);
  let scanError = "";
  if (error.name === "NotAllowedError") {
    scanError = "ERROR: you need to grant camera access permission";
  } else if (error.name === "NotFoundError") {
    scanError = "ERROR: no camera on this device";
  } else if (error.name === "NotSupportedError") {
    scanError = "ERROR: secure context required (HTTPS, localhost)";
  } else if (error.name === "NotReadableError") {
    scanError = "ERROR: is the camera already in use?";
  } else if (error.name === "OverconstrainedError") {
    scanError = "ERROR: installed cameras are not suitable";
  } else if (error.name === "StreamApiNotSupportedError") {
    scanError = "ERROR: Stream API is not supported in this browser";
  } else if (error.name === "InsecureContextError") {
    scanError = "ERROR: Camera access is only permitted in secure context.";
  } else {
    scanError = `ERROR: Camera error (${error.name})`;
  }
  emit("scannedError", scanError);
};

const handleCancel = () => {
  showQR.value = false;
};

// Watch for modal open/close to manage camera enumeration
watch(showQR, async (isOpen) => {
  if (isOpen) {
    // Reset camera state when modal opens
    isCameraReady.value = false;
    isCameraLoading.value = true;

    // Enumerate cameras for all platforms when modal opens
    await enumerateCameras();
  } else {
    // Reset camera state when modal closes
    isCameraReady.value = false;
    isCameraLoading.value = false;
  }
});

// Watch for camera selection changes
watch(selectedCameraId, (newCameraId, oldCameraId) => {
  console.log('Camera selection changed:', { oldCameraId, newCameraId, showQR: showQR.value });
  if (newCameraId && showQR.value) {
    updateCameraConstraints();
  }
});
</script>

<template>
  <div class="flex flex-col items-center justify-center w-full">
    <!-- Scan Button -->
    <AppButton
      :title="label ?? 'Scan'"
      :disabled="disabled"
      :is-loading="isBusy"
      class="w-full md:w-auto my-2"
      @click="showQR = true"
    />

    <!-- QR Scanner Modal -->
    <AppModal
      v-model="showQR"
      :show-save="false"
      cancel-text="Close"
      @cancel="handleCancel"
    >
      <div class="flex flex-col">
        <!-- Header with title and close button -->
        <div class="flex justify-between items-center mb-1">
          <h2 class="text-xl font-semibold">Scan QR Code</h2>
          <button
            @click="handleCancel"
            class="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <i class="fas fa-times text-gray-500"></i>
          </button>
        </div>

        <div class="flex flex-col items-center">

        <!-- Collapsible Camera Settings Section -->
        <div class="w-full max-w-md mb-0">
          <AppLink @click="showCameraSettings = !showCameraSettings" class="text-action text-sm">
            <span>Camera Settings </span>
            <i class="fas" :class="showCameraSettings ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </AppLink>

          <!-- Expandable Content -->
          <div v-if="showCameraSettings" class="mt-2">
            <p class="text-sm text-gray-600 mb-3">
              Changing or resetting the camera may improve quality
            </p>

            <!-- Camera Selection -->
            <div v-if="availableCameras.length > 0" class="mb-3">
              <AppSelect
                v-model="selectedCameraId"
                :options="cameraOptions"
                label="Camera"
                name="camera"
                @update:modelValue="switchCamera"
              />
            </div>

            <!-- Reset Camera Button -->
            <AppButton
              title="Reset Camera"
              buttonType="Secondary"
              :is-loading="isResettingCamera"
              @click="resetCamera"
              class="!mt-0 !mb-0 !py-2 !px-4 text-sm w-full"
            />
          </div>
        </div>

        <!-- Scanning Guidance -->
        <div class="my-1 text-center">
          <p class="text-normal-emphasis text-xs">
            Blurry or low quality? Tap <em>Camera Settings</em> to adjust.
          </p>
        </div>

        <div class="w-full max-w-md overflow-hidden rounded-lg relative">
          <!-- Loading indicator -->
          <div
            v-if="isCameraLoading"
            class="absolute inset-0 bg-gray-100 flex items-center justify-center z-10 rounded-lg"
          >
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-2xl text-gray-500 mb-2"></i>
              <p class="text-sm text-gray-600">Initializing camera...</p>
            </div>
          </div>

          <qrcode-stream
            :key="cameraKey"
            :formats="['qr_code']"
            :constraints="cameraConstraints ?? {facingMode: 'environment'}"
            @detect="scanCode"
            @error="onError"
            @camera-on="onCameraOn"
          />
        </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>
